import { Skeleton } from '@/components/ui/skeleton';
import { InfoTip } from '@/components/inventory/InfoTip';

interface InventoryKpiCardsProps {
  totalCount: number;
  // 부족(가용 기준) 개수 — 판정은 isLowStock 단일 정의(부모가 items에서 파생) — InventoryTable
  // '부족만 보기' 필터 결과 개수와 항상 일치해야 한다.
  lowStockCount: number;
  isItemsLoading: boolean;
  // 최초 items 로드 실패 시 0을 진짜 값처럼 보여주면 안 된다(테이블은 오류 화면인데 타일은
  // "0개"로 갈라짐 — codexloop PR7-2 R1 P2 수용) → "—"로 표시. 부분 성공 후 실패는
  // stale-on-error 관례대로 이전 값 유지(items가 보존되므로 totalCount > 0).
  hasItemsError?: boolean;
}

/**
 * /inventory 상단 KPI 요약 타일 2개(dumb) — 둘 다 클라 파생(items). 오늘 입고/출고 서버
 * 집계 타일(PR7-2)은 오너 결정으로 제거(2026-07-16 개념 다이어트 — 원장 유형을 설명해야
 * 이해되는 숫자, useInventoryTodayKpis 훅·RPC는 보존). 스타일은 이 페이지 카드 관례
 * (rounded-2xl bg-white shadow-sm ring-slate-200) 준수 — 외부 디자인 시스템/차트/그라디언트
 * 도입 금지(PR6 오너 피드백 "완성도·일관성" 선례).
 */
export function InventoryKpiCards({ totalCount, lowStockCount, isItemsLoading, hasItemsError }: InventoryKpiCardsProps) {
  // 최초 로드 중(성공 로드 전)에만 Skeleton — 성공 로드 후 background refresh는 이전 값 유지
  // (useInventory가 hasLoaded 후 isLoading을 올리지 않으므로 이 판정은 초기 로딩에만 참).
  const isItemsInitialLoading = isItemsLoading && totalCount === 0;
  // 최초 로드 실패(items 비어 있는 채 오류) — 0을 실제 값처럼 렌더하지 않는다.
  const isItemsUnavailable = Boolean(hasItemsError) && totalCount === 0;

  return (
    <div className="grid grid-cols-2 gap-2 md:gap-3">
      {/* 라벨→값 2행 동일 구조. 집계 기준(가용 판정)은 상시 캡션 대신 라벨 옆 ⓘ 툴팁 —
          카피 개편 2026-07-16(오너: 캡션이 장황하고 뜻이 안 통함). */}
      <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
        <p className="text-xs text-slate-500">등록 상품</p>
        {isItemsInitialLoading ? (
          <Skeleton className="mt-1 h-7 w-14" />
        ) : (
          <p className="text-xl font-bold tabular-nums text-slate-900">
            {isItemsUnavailable ? '—' : totalCount.toLocaleString('ko-KR')}
          </p>
        )}
      </div>
      <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
        <div className="flex items-center gap-1">
          <p className="text-xs text-slate-500">부족</p>
          <InfoTip ariaLabel="부족 집계 기준">
            {/* 괄호가 조사 앞에 오면 keep-all에서도 ')' 뒤가 개행 허용점이라 '가'가 줄
                첫머리에 고아로 남는다(스크린샷 실측) — 괄호 부연을 문미로 재배열. */}
            가용 재고가 부족 기준 이하인 상품 수 (가용 = 현재고−예약)
          </InfoTip>
        </div>
        {isItemsInitialLoading ? (
          <Skeleton className="mt-1 h-7 w-14" />
        ) : (
          // 0이면 기본색(경고 아님), 1 이상일 때만 amber 강조 — 값만 강조하고 라벨은 그대로.
          <p className={`text-xl font-bold tabular-nums ${lowStockCount > 0 ? 'text-amber-600' : 'text-slate-900'}`}>
            {isItemsUnavailable ? '—' : lowStockCount.toLocaleString('ko-KR')}
          </p>
        )}
      </div>
    </div>
  );
}
